import { z } from 'zod';

export namespace WorkspacesAPI {
  export const transcribeFileRequestParamsSchema = z.object({
    fileId: z.coerce.number(),
    workspaceId: z.coerce.number(),
  });

  export type TranscribeFileRequest = z.infer<typeof transcribeFileRequestParamsSchema>;

  export const parseTranscriptRequestParamsSchema = z.object({
    fileId: z.coerce.number(),
    workspaceId: z.coerce.number(),
  });

  export type ParseTranscriptRequest = z.infer<typeof parseTranscriptRequestParamsSchema>;

  export const preFileUploadCheckRequestBodySchema = z.object({
    files: z.array(
      z.object({
        identifier: z.string(),
        name: z.string(),
        lastModified: z.number().optional(),
        size: z.number().optional(),
        type: z.string().optional(),
      })
    ),
    parentObjectId: z.number(),
  });

  export const preFileUploadCheckRequestParamsSchema = z.object({
    workspaceId: z.coerce.number(),
  });

  export type PreFileUploadCheckRequest = z.infer<typeof preFileUploadCheckRequestBodySchema> &
    z.infer<typeof preFileUploadCheckRequestParamsSchema>;

  export const preFileUploadCheckResponseBodySchema = z.object({
    existing: z.array(z.string()),
    transcribeable: z.array(z.string()),
  });

  export type PreFileUploadCheckResponse = z.infer<typeof preFileUploadCheckResponseBodySchema>;

  /** A chunk as it sits in the file-chunks search index, i.e. what retrieval (and so the agents) sees. */
  export const fileParseDebugChunkSchema = z.object({
    ordinal: z.number(),
    pageNumber: z.number().nullable(),
    nodeType: z.string().nullable(),
    isMarkdown: z.boolean(),
    content: z.string(),
    tags: z.array(z.string()),
  });

  export type FileParseDebugChunk = z.infer<typeof fileParseDebugChunkSchema>;

  /** One slide of a parsed presentation, as stored by the document processor. */
  export const fileParseDebugSlideSchema = z.object({
    ordinal: z.number(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    isContentSlide: z.boolean(),
    isMarkdown: z.boolean(),
    extractionError: z.string().nullable(),
    /** Short-lived signed URL of the rendered slide image the model read, or null if it can't be signed. */
    imageUrl: z.string().nullable(),
  });

  export type FileParseDebugSlide = z.infer<typeof fileParseDebugSlideSchema>;

  export const fileParseDebugRequestParamsSchema = z.object({
    fileId: z.coerce.number().int().positive(),
  });

  export const fileParseDebugResponseBodySchema = z.object({
    file: z.object({
      id: z.number(),
      name: z.string(),
      extension: z.string().nullable(),
    }),
    chunks: z.array(fileParseDebugChunkSchema),
    /** Chunks indexed for the file; greater than `chunks.length` when the list was capped. */
    chunkTotal: z.number(),
    /** Parsed slides for a presentation, or null when the file has none (or they could not be read). */
    powerpoint: z.object({ slides: z.array(fileParseDebugSlideSchema) }).nullable(),
    /** Why `powerpoint` is null although the file has parsed slide data stored. */
    powerpointError: z.string().nullable(),
  });

  export type FileParseDebugResponse = z.infer<typeof fileParseDebugResponseBodySchema>;
}
